import { useState, useRef } from 'react';
import { toast } from 'sonner';
import { api, useAuth, API } from '../App';
import { Camera, Save, Lock, User, Loader2 } from 'lucide-react';

export default function Profil() {
  const { user, updateUser } = useAuth();
  const fileRef = useRef();

  const [form, setForm] = useState({
    ad: user?.ad || '',
    soyad: user?.soyad || '',
    telefon: user?.telefon || '',
  });
  const [sifre, setSifre] = useState({ eski: '', yeni: '', tekrar: '' });
  const [saving, setSaving] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const [uploading, setUploading] = useState(false);

  const avatarSrc = user?.profil_resmi
    ? `${API.replace('/api','')}/uploads/profil/${user.profil_resmi}`
    : null;

  const handleAvatarChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('avatar', file);
    try {
      const { data } = await api.put('/auth/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateUser({ profil_resmi: data.profil_resmi });
      toast.success('Profil fotoğrafı güncellendi');
    } catch {
      toast.error('Yükleme başarısız');
    } finally {
      setUploading(false);
    }
  };

  const handleProfileSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put('/auth/profil', form);
      updateUser(data);
      toast.success('Profil güncellendi');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handlePassSave = async e => {
    e.preventDefault();
    if (sifre.yeni !== sifre.tekrar) return toast.error('Yeni şifreler eşleşmiyor');
    if (sifre.yeni.length < 6) return toast.error('Şifre en az 6 karakter olmalı');
    setSavingPass(true);
    try {
      await api.put('/auth/sifre', { eski_sifre: sifre.eski, yeni_sifre: sifre.yeni });
      toast.success('Şifre güncellendi');
      setSifre({ eski: '', yeni: '', tekrar: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Hata oluştu');
    } finally {
      setSavingPass(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Profilim</h1>
          <p className="page-subtitle">Hesap bilgilerini ve şifreni yönet</p>
        </div>
      </div>

      <div className="profile-grid" style={{ maxWidth: 900 }}>

        {/* Avatar */}
        <div className="card" style={{ padding: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div style={{ position: 'relative' }}>
            {avatarSrc
              ? <img src={avatarSrc} alt="avatar" style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--gray-100)' }} />
              : <div style={{
                  width: 100, height: 100, borderRadius: '50%',
                  background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 32, fontWeight: 700, color: '#fff',
                  border: '3px solid var(--gray-100)'
                }}>
                  {user?.ad?.[0]}{user?.soyad?.[0]}
                </div>
            }
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              style={{
                position: 'absolute', bottom: 0, right: 0,
                width: 32, height: 32, borderRadius: '50%',
                background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
                border: '2px solid #fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff'
              }}
            >
              {uploading ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <Camera size={14} />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleAvatarChange} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--navy)' }}>{user?.ad} {user?.soyad}</div>
            <div style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 4 }}>{user?.email}</div>
            <span className={`badge mt-2 ${user?.rol === 'superadmin' ? 'badge-red' : user?.rol === 'yonetici' ? 'badge-blue' : 'badge-green'}`}>
              {user?.rol}
            </span>
          </div>
          {user?.kurum_adi && (
            <div style={{ textAlign: 'center', borderTop: '1px solid var(--gray-100)', paddingTop: 14, width: '100%' }}>
              <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>Kurum</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', marginTop: 4 }}>{user.kurum_adi}</div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Profil bilgileri */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--gray-100)', paddingBottom: 14 }}>
              <User size={16} color="var(--blue)" />
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>Profil Bilgileri</h2>
            </div>
            <form onSubmit={handleProfileSave}>
              <div className="form-grid">
                <div className="input-group">
                  <label className="input-label">Ad</label>
                  <input className="input" value={form.ad} onChange={e => setForm({...form, ad: e.target.value})} required />
                </div>
                <div className="input-group">
                  <label className="input-label">Soyad</label>
                  <input className="input" value={form.soyad} onChange={e => setForm({...form, soyad: e.target.value})} required />
                </div>
                <div className="input-group full">
                  <label className="input-label">Telefon</label>
                  <input className="input" value={form.telefon} onChange={e => setForm({...form, telefon: e.target.value})} placeholder="05xx xxx xx xx" />
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <div className="spinner" /> : <Save size={15} />}
                  Kaydet
                </button>
              </div>
            </form>
          </div>

          {/* Şifre değiştir */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--gray-100)', paddingBottom: 14 }}>
              <Lock size={16} color="var(--blue)" />
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>Şifre Değiştir</h2>
            </div>
            <form onSubmit={handlePassSave}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="input-group">
                  <label className="input-label">Mevcut Şifre</label>
                  <input className="input" type="password" value={sifre.eski} onChange={e => setSifre({...sifre, eski: e.target.value})} required placeholder="••••••••" />
                </div>
                <div className="input-group">
                  <label className="input-label">Yeni Şifre</label>
                  <input className="input" type="password" value={sifre.yeni} onChange={e => setSifre({...sifre, yeni: e.target.value})} required placeholder="En az 6 karakter" />
                </div>
                <div className="input-group">
                  <label className="input-label">Yeni Şifre (Tekrar)</label>
                  <input className="input" type="password" value={sifre.tekrar} onChange={e => setSifre({...sifre, tekrar: e.target.value})} required placeholder="••••••••" />
                </div>
              </div>
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary" disabled={savingPass}>
                  {savingPass ? <div className="spinner" /> : <Lock size={15} />}
                  Şifreyi Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
